import { useRuntimeConfig } from '#app'
import { isAuthStatusError, isBetterAuthApiKey, isValidApiKey } from '~/lib/johkuApiAuth'
import { isReservedShopId } from '~/lib/reservedShopId'
import { confirmBetterAuthSession } from '~/lib/sessionRecovery'

function settingsQuery (locale: string, deliverymethods = false) {
  return `version=${locale}&pricetiers=true&files=true&alltags=true${deliverymethods ? '&deliverymethods=true' : ''}`
}

function sleep (ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

type SettingsStore = {
  state: { shopId?: string | null, user?: unknown, sessionNeedsRecovery?: boolean }
  getters?: { apiKey?: string | null }
  dispatch: (action: string, payload?: unknown) => unknown
}

type SettingsFetchOptions = {
  headers?: Record<string, string>
  skipAuthRecovery?: boolean
}

// Only a confirmed missing better-auth/legacy session means "logged out".
// A 401/403 from settings.json is not enough: the PHP backend can reject a
// request while get-session still has a valid session (missing Authorization
// header, permission 403, etc.).
async function fetchAuthenticatedSettings (
  url: string,
  apiKey: string,
  store: SettingsStore
): Promise<{ settings: Settings, authRejected: false } | { settings: null, authRejected: boolean }> {
  let lastError: any = null
  let currentKey = apiKey

  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const settings = await $fetch<Settings>(url, {
        headers: { Authorization: 'Bearer ' + currentKey },
        skipAuthRecovery: true
      } as SettingsFetchOptions)
      return { settings, authRejected: false }
    } catch (error) {
      lastError = error
      if (isAuthStatusError(error)) {
        if (attempt === 0 && isBetterAuthApiKey(currentKey)) {
          const refreshed = await confirmBetterAuthSession(store)
          if (refreshed.status === 'ok') {
            currentKey = refreshed.token
            continue
          }
          if (refreshed.status === 'transient') {
            await sleep(300)
            continue
          }
        }
        break
      }
      // Anything else might be a transient blip (dropped connection, timeout,
      // momentary 5xx) - give it one more chance before giving up.
      if (attempt === 0) {
        await sleep(300)
      }
    }
  }

  if (isAuthStatusError(lastError) && isBetterAuthApiKey(currentKey)) {
    const confirmed = await confirmBetterAuthSession(store)
    if (confirmed.status !== 'expired') {
      return { settings: null, authRejected: false }
    }
  }

  return { settings: null, authRejected: isAuthStatusError(lastError) }
}

async function loadShopSettings (
  baseDomain: string,
  shopId: string,
  locale: string,
  apiKey: string | null | undefined,
  store: SettingsStore
): Promise<{ settings: Settings | null, authRejected: boolean }> {
  const baseUrl = `${baseDomain}${shopId}/${locale}/settings`

  if (isValidApiKey(apiKey)) {
    const result = await fetchAuthenticatedSettings(`${baseUrl}.json?${settingsQuery(locale, true)}`, apiKey!, store)
    if (result.settings) {
      return result
    }

    try {
      const settings = await $fetch<Settings>(`${baseUrl}/public.json?${settingsQuery(locale)}`)
      return { settings, authRejected: result.authRejected }
    } catch {
      return { settings: null, authRejected: result.authRejected }
    }
  }

  try {
    const settings = await $fetch<Settings>(`${baseUrl}/public.json?${settingsQuery(locale)}`)
    return { settings, authRejected: false }
  } catch {
    return { settings: null, authRejected: false }
  }
}

async function syncSessionLocale (shopId: string, locale: string) {
  if (!import.meta.server) {
    return
  }
  const event = useRequestEvent()
  if (!event) {
    return
  }
  const { mergeShopSessionLocale } = await import('~/server/utils/session')
  await mergeShopSessionLocale(event, shopId, locale)
}

export default defineNuxtRouteMiddleware(async (to) => {
  const config = useRuntimeConfig()
  const store = useNuxtApp().$store as any
  const pathArr = to.path.split('/')
  // Prefer the router's decoded param over the raw path segment so that shop
  // identifiers with URL-encoded characters (e.g. "%20") match the same
  // (decoded) key used everywhere else (asyncData, computed properties, etc.).
  // Falling back to the raw segment covers non-page paths like "__webpack_hmr".
  const path = typeof to.params.shopid === 'string' ? to.params.shopid : pathArr[1]

  if (path && !isReservedShopId(path)) {
    store.dispatch('setShopId', path)

    const pendingStaffAccess = to.query.staff_access === '1'
    if (pendingStaffAccess) {
      store.dispatch('setSessionExpired', false)
      store.dispatch('setUser', null)
    }

    const systemLocale = store.state.locale

    if (store.state.user) {
      await store.dispatch('userdata')
    }

    const userLocale = store.state.locale
    if (systemLocale !== userLocale) {
      await store.dispatch('setUserLocale', userLocale)
    }

    let locale = store.state.locale || 'fi_FI'

    const cachedSettings = store.state.settings[`${path}-${locale}`]
    let settings: Settings | null = cachedSettings?.id ? cachedSettings : null

    if (!settings) {
      const apiKey = store.getters.apiKey

      try {
        const settingsResult = await loadShopSettings(
          config.public.baseDomain,
          path,
          locale,
          apiKey,
          store
        )

        if (settingsResult.authRejected) {
          store.dispatch('setUser', null)
          if (pendingStaffAccess) {
            store.dispatch('setSessionExpired', false)
          } else {
            store.dispatch('setAuthToken', null)
            store.dispatch('setSessionExpired', true)
          }

          if (import.meta.server) {
            const event = useRequestEvent()
            if (event) {
              const { getUserSessionForShop, replaceUserSessionForShop } = await import('~/server/utils/session')
              const session = await getUserSessionForShop(event, path)
              await replaceUserSessionForShop(event, path, {
                clientid: session?.clientid ?? null,
                shopId: path,
                locale: session?.locale
              })
            }
          }
        } else {
          store.dispatch('setSessionExpired', false)
        }

        const [linkedStores, vouchers] = await Promise.all([
          $fetch(`${config.public.baseDomain}${path}/${locale}/linkedstores/public.json?version=${locale}`).catch(() => {
            console.log(path, 'Linked store not found')
            store.dispatch('setLinkedStores', {
              linkedStores: []
            })
            return null
          }) as Promise<LinkedStore[]>,
          $fetch(`${config.public.baseDomain}${path}/${locale}/vouchers.json?iSortingCols=1&iSortDir_0=2`).catch(() => {
            console.log(path, 'Vouchers not found')
            store.dispatch('setVouchers', {
              vouchers: []
            })
            return null
          }) as Promise<Voucher[]>,
        ])

        if (settingsResult.settings) {
          settings = settingsResult.settings
          store.dispatch('setSettings', {
            shopId: path,
            locale,
            settings: settingsResult.settings
          })
        } else {
          console.log(path, 'Settings not found')
          settings = {}
          store.dispatch('setSettings', {
            shopId: path,
            locale,
            settings: {}
          })
        }

        if (linkedStores) {
          store.dispatch('setLinkedStores', {
            linkedStores
          })

          if (linkedStores.length) {
            const results = await Promise.allSettled(
              linkedStores.map((ls: { shopid: string }) =>
                $fetch<Settings>(`${config.public.serviceDomain}${ls.shopid}/${locale}/settings/public.json?version=${locale}&pricetiers=true${store.state.clientid ? `&deliverymethods=true&client=${store.state.clientid}&shop=${path}` : ''}`)
              )
            )

            results.forEach((result, i) => {
              if (result.status === 'fulfilled' && result.value && linkedStores[i]) {
                store.dispatch('setSettings', {
                  shopId: linkedStores[i].shopid,
                  locale,
                  settings: result.value
                })
              }
            })
          }
        }

        if (vouchers) {
          store.dispatch('setVouchers', {
            vouchers
          })
        }
      } catch (e) {
        console.log(path, 'Error loading settings.', e)
        settings = {}
        store.dispatch('setSettings', {
          shopId: path,
          locale,
          settings: {}
        })
      }
    }

    // When the user has no personal language override, shop Settings UI language
    // is the system locale source of truth (not the sticky session / defaultversion).
    const shopLocale = settings?.locale
    const hasUserLanguageOverride = !!(store.state.userData?.language)
    if (shopLocale && !hasUserLanguageOverride && shopLocale !== store.state.systemLocale) {
      await store.dispatch('setLocale', shopLocale)
      await syncSessionLocale(path, shopLocale)

      if (!store.state.settings[`${path}-${shopLocale}`]?.id) {
        store.dispatch('setSettings', {
          shopId: path,
          locale: shopLocale,
          settings
        })
      }
      locale = shopLocale
    }
  }
})
